import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { isRedirectToCartOnAssetShareEnabled } from '@/feature/feature';
import { useAppStore } from '@/store/app.store';
import ListView from '@ingka/list-view';
import SkipContent from '@ingka/skip-content';
import { selectCartData } from '@repo/api-adapters/selectors/cart.selectors';
import {
    selectAssetAlias,
    selectAssetCreationDate,
    selectAssetDetails,
    selectAssetStatus,
    selectBuCode,
    selectCreatedBy,
    selectCustomerMeetingPoint,
    selectOrderType,
    selectPaymentPlanStatus,
} from '@repo/api-adapters/selectors/customer-assets.selectors';
import { selectDeliveryContact } from '@repo/api-adapters/selectors/customer.selectors';
import { useStoreDetails } from '@repo/hooks';
import { translate } from '@repo/phrase';
import { formatDateTime, isNotFoundError } from '@repo/utils';
import { CustomerInformationCard, CustomerInformationDetails } from '@sp-saljapro/ot-customer-information';

import { setAssetTelemetryContext } from '@/lib/telemetry.lib';

import { ExtraNeeds } from '@/components/asset-details/extra-needs';
import { AttachmentsSection } from '@/components/attachments/attachments-section.component';
import { CustomerCard } from '@/components/customer-card';
import { LoadingPage } from '@/components/loading';
import { QueryError } from '@/components/query-error';
import { ShareHeader } from '@/components/share/share-header';
import { StatusHeader } from '@/components/status-header';

import { getServiceEndpoint } from '@/api/helpers.api';
import { useAsset, useAssetParams } from '@/hooks/asset.hooks';
import { useAppBaseUrl } from '@/hooks/router.hooks';
import { formatDeliveryAddress } from '@/utils/services.utils';
import { getAppConfig } from '@/utils/window.utils.ts';

import { NoResult } from '../no-result';
import { AssetDetailsContent } from './asset-details-content/asset-details-content.component';
import { DrawingsSection } from './asset-drawings/drawings-section.component';
import { AssetPriceSummaryBox } from './asset-price-summary-box/asset-price-summary-box.component';
import { DocuSignSection } from './docusign-section/docusign-section.component';
import { PaymentMethods } from './payment-methods';
import { QuotationSettings } from './quotation-settings';
import styles from './styles.module.scss';

export const AssetDetails = () => {
    const { assetType, assetId } = useAssetParams();
    const resetAssetDetailsView = useAppStore((state) => state.resetAssetDetailsView);
    const { data, isError, error } = useAsset(assetType, assetId);

    const orderType = selectOrderType(data);
    const assetAlias = selectAssetAlias(data);
    const statusHeaderAssetType = orderType ?? (assetType === 'draft' ? 'DRAFT' : 'SALJAPRO_QUOTATION');

    useEffect(() => {
        resetAssetDetailsView();
    }, [assetId, assetType, resetAssetDetailsView]);

    useEffect(() => {
        const assetTypeTitle = {
            order: translate('order_status_header_order_id'),
            draft: translate('order_status_header_draft_id'),
            quote: translate('order_status_header_quotation_id'),
        }[assetType];
        document.title = `${assetTypeTitle} ${assetId}`;
    }, [assetId, assetType]);

    const assetBuCode = selectBuCode(data) ?? '';
    const config = getAppConfig();
    const storeDetailsBaseUrl = getServiceEndpoint(config.api.services.classicSalja, 'storeDetails');
    const enableCustomerInformation = config.feature.customerInformation?.enable;
    const { data: storeDetails } = useStoreDetails(storeDetailsBaseUrl, assetBuCode);
    const storeTimezone = storeDetails?.TimeZone ?? '';

    const baseUrl = useAppBaseUrl();
    const navigate = useNavigate();

    const handleNavigate = () => {
        return () => {
            !isRedirectToCartOnAssetShareEnabled() &&
                void navigate(`${baseUrl}/view/${assetType}/${assetId}`, { replace: true });
        };
    };

    const [searchParams] = useSearchParams();
    const showShareHeader = searchParams.get('confirmation') === 'true';
    const assetStatus = selectAssetStatus(data);

    useEffect(() => {
        if (!data) {
            setAssetTelemetryContext();
            return;
        }

        setAssetTelemetryContext({
            id: assetId,
            type: assetType,
            viewContext: showShareHeader ? 'confirmation' : 'details',
            ...(orderType && { orderType }),
            ...(assetStatus && { status: assetStatus }),
        });

        return () => {
            setAssetTelemetryContext();
        };
    }, [assetId, assetStatus, assetType, data, orderType, showShareHeader]);

    const [customerDetailsModalOpen, setCustomerDetailsModalOpen] = useState(false);

    if (!data && isNotFoundError(error)) {
        return <NoResult message={translate('no_result_message_order_not_found', { orderId: assetId })} />;
    }

    if (!data && isError) {
        return <QueryError error={error} />;
    }

    if (!data) {
        return <LoadingPage />;
    }

    const assetDetails = selectAssetDetails(data);
    const cart = selectCartData(data);

    if (!assetDetails || !cart) {
        return <NoResult message={translate('no_result_message_order_not_found', { orderId: assetId })} />;
    }

    const createdAt = formatDateTime(selectAssetCreationDate(data) ?? '', getAppConfig().dateTimeFormat, storeTimezone);
    const createdBy = selectCreatedBy(data);
    const customerMeetingPoint = selectCustomerMeetingPoint(data);
    const deliveryContact = selectDeliveryContact(data);
    const paymentPlanStatus = selectPaymentPlanStatus(data);

    const renderShareHeader = () => {
        if (!showShareHeader) {
            return null;
        }

        return (
            <ShareHeader
                data={data}
                onClose={handleNavigate()}
                initialEmail={deliveryContact?.email}
                initialSms={deliveryContact?.mobilePhone}
            />
        );
    };

    return (
        <div data-testid={`${assetType}-details`} id="content-asset-details">
            <SkipContent
                label={translate('skip_content_target_name', {
                    targetName: translate('skip_content_asset_details_content'),
                })}
                targetId="content-asset-details-content"
            />
            <StatusHeader
                type={statusHeaderAssetType}
                orderAlias={assetAlias}
                status={assetStatus}
                createdAt={createdAt}
                createdBy={createdBy}
                cmpName={customerMeetingPoint}
                paymentStatus={paymentPlanStatus}
            />
            {renderShareHeader()}
            <div className={styles.asset_details_content}>
                <div className={styles.asset_details_table} id="content-asset-details-content">
                    <SkipContent
                        label={translate('skip_content_target_name', { targetName: translate('skip_content_sidebar') })}
                        targetId="content-sidebar"
                    />
                    <AssetDetailsContent />
                </div>
                <div className={styles.asset_details_sidebar}>
                    <SkipContent
                        label={translate('skip_content_target_name', {
                            targetName: translate('skip_content_asset_details'),
                        })}
                        targetId="content-asset-details"
                    />
                    <div id="content-sidebar" className={styles.asset_details_sidebar_container}>
                        {enableCustomerInformation ? (
                            <>
                                <CustomerInformationCard
                                    customerData={{
                                        businessName: deliveryContact?.businessName,
                                        email: deliveryContact?.email,
                                        familyNumber:
                                            deliveryContact?.ikeaFamilyNumber ?? deliveryContact?.businessLoyaltyCardNo,
                                        firstName: deliveryContact?.firstName,
                                        isBusinessCustomer: deliveryContact?.isBusinessCustomer,
                                        lastName: deliveryContact?.lastName,
                                        mobile: deliveryContact?.mobilePhone,
                                        address: formatDeliveryAddress({
                                            addressLine1: deliveryContact?.addressLine1,
                                            addressLine2: deliveryContact?.addressLine2,
                                            city: deliveryContact?.city,
                                            zipCode: deliveryContact?.zipCode,
                                            stateCode: deliveryContact?.stateCode,
                                            countryCode: deliveryContact?.countryCode,
                                        }),
                                        isIbn: deliveryContact?.isIBN,
                                    }}
                                    translations={{
                                        businessNetworkLabel: translate('order_sidebar_business_network'),
                                        familyMemberLabel: translate('order_sidebar_family_member'),
                                    }}
                                    modalConfig={{
                                        canChange: false,
                                        onClick: () => {
                                            setCustomerDetailsModalOpen(true);
                                        },
                                    }}
                                />

                                <CustomerInformationDetails
                                    customerData={{
                                        addressLine1: deliveryContact?.addressLine1,
                                        addressLine2: deliveryContact?.addressLine2,
                                        businessName: deliveryContact?.businessName,
                                        city: deliveryContact?.city,
                                        email: deliveryContact?.email,
                                        firstName: deliveryContact?.firstName,
                                        lastName: deliveryContact?.lastName,
                                        mobile: deliveryContact?.mobilePhone,
                                        zipCode: deliveryContact?.zipCode,
                                    }}
                                    translations={{
                                        title: translate('customer_information_details_title'),
                                        addressLine1Label: translate('customer_information_details_address_line_1'),
                                        addressLine2Label: translate('customer_information_details_address_line_2'),
                                        cityLabel: translate('customer_information_details_city'),
                                        emailLabel: translate('customer_information_details_email'),
                                        firstNameLabel: translate('customer_information_details_first_name'),
                                        lastNameLabel: translate('customer_information_details_last_name'),
                                        phoneLabel: translate('customer_information_details_phone'),
                                        zipCodeLabel: translate('customer_information_details_zip_code'),
                                        businessNameLabel: translate('customer_information_details_business_name'),
                                    }}
                                    modalConfig={{
                                        readonlyMode: true,
                                        isOpen: customerDetailsModalOpen,
                                        onClose: () => {
                                            setCustomerDetailsModalOpen(false);
                                        },
                                    }}
                                />
                            </>
                        ) : (
                            <CustomerCard
                                addressLine1={deliveryContact?.addressLine1}
                                addressLine2={deliveryContact?.addressLine2}
                                businessName={deliveryContact?.businessName}
                                city={deliveryContact?.city}
                                countryCode={deliveryContact?.countryCode}
                                email={deliveryContact?.email}
                                // TODO(ASOT-5802): remove the temporary workaround below (see: https://jira.digital.ingka.com/browse/ASOT-5802)
                                familyNumber={
                                    deliveryContact?.ikeaFamilyNumber ?? deliveryContact?.businessLoyaltyCardNo
                                }
                                firstName={deliveryContact?.firstName}
                                isBusinessCustomer={deliveryContact?.isBusinessCustomer}
                                lastName={deliveryContact?.lastName}
                                mobile={deliveryContact?.mobilePhone}
                                stateCode={deliveryContact?.stateCode}
                                zipCode={deliveryContact?.zipCode}
                                isIbn={deliveryContact?.isIBN}
                            />
                        )}
                        <ListView id="asset-services-methods" className={String(styles.asset_methods)}>
                            <DrawingsSection />
                            <AttachmentsSection />
                            <DocuSignSection />
                            {assetType === 'order' && (
                                <div>
                                    <PaymentMethods />
                                </div>
                            )}{' '}
                            {assetType === 'draft' && <ExtraNeeds />}
                            {assetType === 'quote' && <QuotationSettings />}
                        </ListView>
                    </div>
                    <AssetPriceSummaryBox />
                </div>
            </div>
        </div>
    );
};
